import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Loader2,
  Download,
  FileText,
  Copy,
  Edit3,
  Save,
  RefreshCw,
  Mail
} from "lucide-react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import Letterhead from "@/components/Letterhead";
import SpeechText from "@/components/SpeechText";
import { trackPurchase, trackPdfDownload } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  const [activeDraft, setActiveDraft] = useState(1);
  const [saveEmail, setSaveEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);

  // Prefill email from form
  useEffect(() => {
    const formEmail = localStorage.getItem("mf_checkout_email");
    if (formEmail) setSaveEmail(formEmail);
  }, []);

  const sessionId = searchParams.get("session_id");

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentStatus(response.data);

      if (response.data.payment_status === "paid") {
        // Fetch the order content
        const orderResponse = await axios.get(`${API}/orders/${response.data.order_id}/download`);
        setOrderData(orderResponse.data);

        // Track purchase event in GA4 (once per page load)
        if (!window.__mf_purchase_tracked) {
          window.__mf_purchase_tracked = true;
          const firstItem = orderResponse.data.items?.[0];
          trackPurchase(
            response.data.order_id,
            orderResponse.data.total_price_dkk,
            firstItem?.length_type || "unknown"
          );
        }
        
        // Initialize edited content with original content
        const initialEdited = {};
        orderResponse.data.items.forEach(item => {
          initialEdited[item.id] = item.full_content;
        });
        setEditedContent(initialEdited);
        
        setLoading(false);
      } else if (response.data.status === "expired") {
        toast.error("Betalingssessionen er udløbet. Prøv igen.");
        setLoading(false);
      } else {
        // Continue polling
        if (pollCount < 10) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      if (pollCount < 5) {
        setTimeout(() => setPollCount(prev => prev + 1), 2000);
      } else {
        toast.error("Kunne ikke bekræfte betaling. Kontakt support.");
        setLoading(false);
      }
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopieret til udklipsholder!");
  };

  const downloadAsPDF = (elementId, recipientName, contentType) => {
    const element = document.getElementById(elementId);
    if (!element) {
      toast.error("Kunne ikke finde indholdet");
      return;
    }

    trackPdfDownload();

    const safeName = (recipientName || 'tale').toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/æ/g, "ae")
      .replace(/ø/g, "oe")
      .replace(/å/g, "aa");
    const filename = `festtale_${safeName}.pdf`;

    html2pdf()
      .set({
        margin: 10,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(element)
      .save();
  };

  const startEditing = () => {};
  const saveEditing = () => {};
  const cancelEditing = () => {};

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Bekræfter betaling...</p>
          <p className="text-sm text-muted-foreground mt-2">Dette kan tage et øjeblik</p>
        </div>
      </div>
    );
  }

  if (!orderData || paymentStatus?.payment_status !== "paid") {
    // Check if we're in a state where payment might still be processing
    const isProcessing = paymentStatus?.status === "open" || !paymentStatus;
    
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="max-w-xl mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className={`h-8 w-8 text-yellow-600 ${isProcessing ? 'animate-spin' : ''}`} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-primary mb-4">
            {isProcessing ? "Afventer betalingsbekræftelse..." : "Betaling ikke bekræftet"}
          </h1>
          <p className="text-muted-foreground mb-4">
            {isProcessing 
              ? "Vi venter på bekræftelse fra Stripe. Dette kan tage op til 30 sekunder."
              : "Vi kunne ikke bekræfte din betaling. Hvis du har betalt, vent venligst et øjeblik og genindlæs siden."
            }
          </p>
          {paymentStatus && (
            <p className="text-sm text-muted-foreground mb-8 bg-muted p-3 rounded-lg">
              <strong>Session status:</strong> {paymentStatus.status || "ukendt"}<br/>
              <strong>Betalingsstatus:</strong> {paymentStatus.payment_status || "afventer"}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Genindlæs siden
            </Button>
            <Link to="/">
              <Button>Tilbage til forsiden</Button>
            </Link>
          </div>
          
          {/* Help text */}
          <div className="mt-8 text-left bg-muted/50 p-4 rounded-lg">
            <h3 className="font-semibold text-sm mb-2">Har du betalt?</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Tjek din e-mail for en kvittering fra Stripe</li>
              <li>• Vent 30 sekunder og genindlæs siden</li>
              <li>• Kontakt os på support@minfesttale.dk hvis problemet fortsætter</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-accent" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary">
            Tak for dit køb!
          </h1>
          <p className="text-muted-foreground mt-2">
            Din tale er klar — du kan tilpasse og downloade den nedenfor
          </p>
        </div>

        {/* Edit info banner */}
        <Card className="mb-8 bg-secondary/5 border-secondary/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Edit3 className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-medium text-primary">Tilpas teksten selv</h3>
                  <p className="text-sm text-muted-foreground">
                    Din tale er et udgangspunkt — klik på "Rediger" for at tilpasse den til den er perfekt
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Save email for future access */}
            {!emailSaved && orderData?.order_id && (
              <Card className="mb-8 bg-primary/5 border-primary/20" data-testid="save-email-card">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-primary">Gem adgang til din tale</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Indtast din email så du kan vende tilbage og redigere din tale i 90 dage.
                      </p>
                      <form className="flex gap-2" onSubmit={async (e) => {
                        e.preventDefault();
                        if (!saveEmail.trim()) return;
                        try {
                          await axios.post(`${API}/api/orders/save-email`, { 
                            order_id: orderData.order_id, 
                            email: saveEmail 
                          });
                          setEmailSaved(true);
                          toast.success("Email gemt! Du kan nu logge ind på minfesttale.dk/login");
                        } catch {
                          toast.error("Kunne ikke gemme email. Prøv igen.");
                        }
                      }}>
                        <Input
                          type="email"
                          placeholder="din@email.dk"
                          value={saveEmail}
                          onChange={(e) => setSaveEmail(e.target.value)}
                          required
                          className="h-10 rounded-lg flex-1"
                          data-testid="save-email-input"
                        />
                        <Button type="submit" size="sm" className="h-10 rounded-lg bg-primary" data-testid="save-email-btn">
                          Gem
                        </Button>
                      </form>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {emailSaved && (
              <Card className="mb-8 bg-accent/5 border-accent/20">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
                  <p className="text-sm text-primary">
                    Email gemt! Log ind på <strong>/login</strong> med denne email for at vende tilbage til din tale.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Content cards */}
            <div className="space-y-8">
              {orderData.items.map((item) => {
                const hasDraft2 = !!item.full_content_draft2;
                const currentContent = activeDraft === 2 && hasDraft2
                  ? (editedContent[`${item.id}_draft2`] ?? item.full_content_draft2)
                  : (editedContent[item.id] ?? item.full_content);
                const editKey = activeDraft === 2 && hasDraft2 ? `${item.id}_draft2` : item.id;
                const originalForDraft = activeDraft === 2 && hasDraft2
                  ? item.full_content_draft2
                  : item.full_content;

                return (
                  <div key={item.id} data-testid={`download-card-${item.content_type}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-6 w-6 text-primary" />
                  <div>
                    <h2 className="font-heading text-xl font-bold text-primary">
                      Din festtale
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Til {item.recipient_name}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {editingId !== editKey ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingId(editKey);
                          if (!editedContent[editKey]) {
                            setEditedContent(prev => ({ ...prev, [editKey]: originalForDraft }));
                          }
                        }}
                        data-testid={`edit-btn-${item.content_type}`}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Rediger
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(currentContent)}
                        data-testid={`copy-btn-${item.content_type}`}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Kopiér
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => downloadAsPDF(`letterhead-${item.id}`, item.recipient_name, item.content_type)}
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`download-btn-${item.content_type}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditedContent(prev => ({ ...prev, [editKey]: originalForDraft }));
                          setEditingId(null);
                        }}
                      >
                        Annuller
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditingId(null);
                          toast.success("Dine ændringer er gemt!");
                        }}
                        className="bg-accent hover:bg-accent/90"
                        data-testid={`save-btn-${item.content_type}`}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Gem ændringer
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Draft tabs */}
              {hasDraft2 && (
                <div className="flex gap-2 mb-4" data-testid="success-draft-tabs">
                  <button
                    onClick={() => { setActiveDraft(1); setEditingId(null); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeDraft === 1
                        ? "bg-primary text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid="success-draft-tab-1"
                  >
                    Udkast 1
                  </button>
                  <button
                    onClick={() => { setActiveDraft(2); setEditingId(null); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeDraft === 2
                        ? "bg-primary text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid="success-draft-tab-2"
                  >
                    Udkast 2
                  </button>
                  <span className="flex items-center text-xs text-muted-foreground ml-2">
                    Mix og match fra begge udkast
                  </span>
                </div>
              )}
              
              {/* Content in Letterhead or Editor */}
              {editingId === editKey ? (
                <Card className="border-2 border-accent">
                  <CardContent className="p-4">
                    <Textarea
                      value={editedContent[editKey] || currentContent || ""}
                      onChange={(e) => setEditedContent(prev => ({ ...prev, [editKey]: e.target.value }))}
                      className="min-h-[400px] font-body text-base leading-relaxed resize-y"
                      data-testid={`editor-${item.content_type}`}
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Tip: Du kan kopiere tekst fra det andet udkast og indsætte det her.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div id={`letterhead-${item.id}`}>
                  <Letterhead
                    title={item.speech_title || "Festtale"}
                    subtitle={`Til ${item.recipient_name}`}
                    type={item.content_type}
                  >
                    <SpeechText text={currentContent} forPdf={true} />
                  </Letterhead>
                </div>
              )}
            </div>
              );
              })}
        </div>

        {/* Satisfaction guarantee */}
        <Card className="mt-8 bg-muted/30 border-border/40">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-primary mb-2">
                  100% tilfredshedsgaranti
                </h3>
                <p className="text-muted-foreground text-sm">
                  Ikke helt tilfreds med din tale? Inden for 24 timer kan du få genereret 
                  en helt ny version gratis. Kontakt os på support@minfesttale.dk.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips section */}
        <Card className="mt-6 bg-muted/30 border-border/40">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-bold text-primary mb-4">
              Tips til at holde din tale
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">1.</span>
                <span>Øv talen højt flere gange — det gør en kæmpe forskel!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">2.</span>
                <span>Tag pauser ved vigtige pointer. Det giver mere vægt.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">3.</span>
                <span>Kig på personen du taler til — ikke kun på papiret.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">4.</span>
                <span>Det er okay at blive rørt. Det viser at det kommer fra hjertet.</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Back to home */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              Tilbage til forsiden
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
